import { useState, useEffect, useRef, useCallback } from 'react';

export const useOctaveWorker = () => {
  const [isReady, setIsReady] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const workerRef = useRef(null);
  const executionIdRef = useRef(0);
  const callbacksRef = useRef({});

  useEffect(() => {
    // Create the worker
    const worker = new Worker('/workers/octave-worker.js');
    workerRef.current = worker;

    // Handle messages from the worker
    worker.onmessage = (e) => {
      const { type, id, data, result, error, executionTimeMs } = e.data;

      switch (type) {
        case 'ready':
          setIsReady(true);
          break;

        case 'stdout':
          if (callbacksRef.current[id]?.onStdout) {
            callbacksRef.current[id].onStdout(data);
          }
          break;

        case 'stderr':
          if (callbacksRef.current[id]?.onStderr) {
            callbacksRef.current[id].onStderr(data);
          }
          break;

        case 'artifacts':
          if (callbacksRef.current[id]?.onArtifacts) {
            callbacksRef.current[id].onArtifacts(data);
          }
          break;

        case 'complete':
          setIsExecuting(false);
          if (callbacksRef.current[id]?.onComplete) {
            callbacksRef.current[id].onComplete(result, executionTimeMs);
          }
          delete callbacksRef.current[id];
          break;

        case 'error':
          setIsExecuting(false);
          if (callbacksRef.current[id]?.onError) {
            callbacksRef.current[id].onError(error, executionTimeMs);
          }
          delete callbacksRef.current[id];
          break;

        default:
          console.warn('Unknown message type from worker:', type);
      }
    };

    worker.onerror = (error) => {
      console.error('Worker error:', error);
      setIsReady(false);
      setIsExecuting(false);
    };

    // Cleanup on unmount
    return () => {
      worker.terminate();
    };
  }, []);

  const execute = useCallback((code, callbacks = {}) => {
    if (!isReady) {
      callbacks.onError?.('Octave is not ready yet');
      return;
    }

    if (isExecuting) {
      callbacks.onError?.('Another execution is in progress');
      return;
    }

    const id = ++executionIdRef.current;
    callbacksRef.current[id] = callbacks;
    setIsExecuting(true);

    workerRef.current.postMessage({
      type: 'execute',
      code,
      id
    });

    return id;
  }, [isReady, isExecuting]);

  const cancelExecution = useCallback(() => {
    if (!isExecuting) return;

    console.log('[useOctaveWorker] Cancelling execution...');

    // Terminate the current worker
    if (workerRef.current) {
      workerRef.current.terminate();
    }

    // Clear all pending callbacks
    Object.keys(callbacksRef.current).forEach(id => {
      callbacksRef.current[id]?.onError?.('Execution cancelled by user');
      delete callbacksRef.current[id];
    });

    // Reset state
    setIsExecuting(false);
    setIsReady(false);

    // Reinitialize the worker
    const worker = new Worker('/workers/octave-worker.js');
    workerRef.current = worker;

    // Re-setup message handlers
    worker.onmessage = (e) => {
      const { type, id, data, result, error, executionTimeMs } = e.data;

      switch (type) {
        case 'ready':
          setIsReady(true);
          break;

        case 'stdout':
          if (callbacksRef.current[id]?.onStdout) {
            callbacksRef.current[id].onStdout(data);
          }
          break;

        case 'stderr':
          if (callbacksRef.current[id]?.onStderr) {
            callbacksRef.current[id].onStderr(data);
          }
          break;

        case 'artifacts':
          if (callbacksRef.current[id]?.onArtifacts) {
            callbacksRef.current[id].onArtifacts(data);
          }
          break;

        case 'complete':
          setIsExecuting(false);
          if (callbacksRef.current[id]?.onComplete) {
            callbacksRef.current[id].onComplete(result, executionTimeMs);
          }
          delete callbacksRef.current[id];
          break;

        case 'error':
          setIsExecuting(false);
          if (callbacksRef.current[id]?.onError) {
            callbacksRef.current[id].onError(error, executionTimeMs);
          }
          delete callbacksRef.current[id];
          break;

        default:
          console.warn('Unknown message type from worker:', type);
      }
    };

    worker.onerror = (error) => {
      console.error('Worker error:', error);
      setIsReady(false);
      setIsExecuting(false);
    };
  }, [isExecuting]);

  const resetExecutionState = useCallback(() => {
    console.log('[useOctaveWorker] Force resetting execution state...');
    setIsExecuting(false);

    // Clear all pending callbacks
    Object.keys(callbacksRef.current).forEach(id => {
      delete callbacksRef.current[id];
    });
  }, []);

  return {
    isReady,
    isExecuting,
    execute,
    cancelExecution,
    resetExecutionState
  };
};
